// Client-side inventory permission mirror (Module 06 §7).
//
// The server is the ONLY authorisation boundary: every mutation is authorised
// API-side (`@RequiresPermission("inventory.*")`), the parenthesised §7 cell
// narrowings (storekeeper `≤ threshold`, planner/procurement `create` only, qc
// `qc_fail` cuts, sales own-orders/own-customers, floor_worker `count only`)
// are re-checked in the service layer, and money fields are redacted in the
// serializer. This hook exists so the UI does not OVER-SHOW controls a role
// cannot use — reverse a movement, approve a count, re-parent a location. A
// hidden control is a convenience, never a security guarantee.
//
// It reads the caller's effective `inventory.*` codes off the permission list
// `GET /v1/auth/tenants` carries (D-586), so a tenant's clone of a seed role is
// offered what it holds. The table below mirrors the authoritative grants in
// `packages/contracts/src/permissions.ts` (`systemRoleGrants`, the inventory.*
// slice) and answers only for a feed row without that list.
//
// `has()` is FAIL-CLOSED: false until the role feed settles and false if it
// fails.
//
// IMPORTANT — `inventory.cost.read` is a FIELD grant, not a screen gate. It is
// listed here for completeness (and for the ATP/ledger nav decisions), but no
// screen may branch its money rendering on it: redaction is decided by what the
// SERVER actually sent (see `lib/cost.ts`). A client-side `has("cost.read")`
// would silently disagree with the server for custom roles.

import {
  derivePermissions,
  holdsPermission,
  roleSignalOf,
  useRoleCodesFeed,
  type RoleInput,
  type RoleSignal,
  type AreaPermissions,
} from "../../../../lib/role-codes";

/** The 15 `inventory.*` codes + the `inventory.cost.read` field grant (§7). */
export const INVENTORY_PERMISSIONS = [
  "inventory.read",
  "inventory.location.manage",
  "inventory.receipt.create",
  "inventory.unit.cut",
  "inventory.scan.locate",
  "inventory.adjust",
  "inventory.movement.reverse",
  "inventory.reservation.manage",
  "inventory.transfer.manage",
  "inventory.transfer.receive",
  "inventory.quarantine",
  "inventory.count.perform",
  "inventory.count.review",
  "inventory.trace.read",
  "inventory.count_schedule.manage",
  "inventory.cost.read",
] as const;

export type InventoryPermission = (typeof INVENTORY_PERMISSIONS)[number];

const ALL: readonly string[] = INVENTORY_PERMISSIONS;

/**
 * Role code → `inventory.*` codes: the inventory slice of the contracts
 * `systemRoleGrants` table (spec 06 §7). It MUST stay in step with that table —
 * the server enforces the real grants; this only governs what the UI offers.
 *
 * The rows that are easy to get wrong, all straight from §7 / the contracts:
 * - group_admin holds every code (`nonPlatformCodes` by construction), and
 *   facility_admin holds every INVENTORY code (the §7 matrix has ✓ in every
 *   facility_admin cell, incl. the cost.read field grant).
 * - planner/procurement hold `transfer.manage` but only the CREATE half —
 *   dispatch is service-narrowed, so the client mirror cannot express it and
 *   the dispatch button is offered on `transfer.manage` and refused server-side
 *   for those roles. §4.4 states this.
 * - storekeeper does NOT hold `count.review`, `movement.reverse`, `quarantine`
 *   or `cost.read` by default (count.review is grantable, not seeded).
 * - floor_worker holds NO `trace.read` (§7 gives it 403 on trace endpoints) and
 *   no `reservation.manage`.
 * - finance holds `movement.reverse`, `count.review`, `trace.read` and the
 *   `cost.read` field grant, but no warehouse-operation codes.
 */
export const INVENTORY_ROLE_GRANTS: Readonly<Record<string, readonly string[]>> = {
  group_admin: ALL,
  facility_admin: ALL,
  planner: [
    "inventory.read",
    "inventory.reservation.manage",
    "inventory.transfer.manage",
    "inventory.trace.read",
  ],
  sales: ["inventory.read", "inventory.reservation.manage", "inventory.trace.read"],
  procurement: [
    "inventory.read",
    "inventory.receipt.create",
    "inventory.reservation.manage",
    "inventory.transfer.manage",
    "inventory.trace.read",
  ],
  finance: [
    "inventory.read",
    "inventory.movement.reverse",
    "inventory.count.review",
    "inventory.trace.read",
    "inventory.cost.read",
  ],
  qc: [
    "inventory.read",
    "inventory.unit.cut",
    "inventory.scan.locate",
    "inventory.quarantine",
    "inventory.trace.read",
  ],
  maintenance: ["inventory.read", "inventory.scan.locate"],
  storekeeper: [
    "inventory.read",
    "inventory.location.manage",
    "inventory.receipt.create",
    "inventory.unit.cut",
    "inventory.scan.locate",
    "inventory.adjust",
    "inventory.reservation.manage",
    "inventory.transfer.manage",
    "inventory.transfer.receive",
    "inventory.count.perform",
    "inventory.trace.read",
    "inventory.count_schedule.manage",
  ],
  floor_worker: [
    "inventory.read",
    "inventory.unit.cut",
    "inventory.scan.locate",
    "inventory.transfer.receive",
    "inventory.count.perform",
  ],
};

/**
 * The effective `inventory.*` set for a role-code set (pure, unit-tested).
 * Union of every known role's grants; unknown/custom roles add nothing.
 */
export function deriveInventoryPermissions(roles: RoleInput): Set<string> {
  return derivePermissions(roles, INVENTORY_ROLE_GRANTS);
}

/**
 * Roles holding `identity.view_members` (`packages/contracts/src/permissions.ts`
 * — group_admin has every non-platform code, facility_admin every `identity.*`
 * except manage_roles / manage_security_policy). Not an inventory code, but the
 * inventory screens need it to decide whether the member directory is readable
 * at all: resolving a performer's NAME costs a `GET /v1/members` that 403s for
 * every warehouse role, and firing a call we know will be refused is the dead
 * call the doctrine forbids. The feed's permission list answers this (D-586);
 * the role set answers only for a feed row without one.
 */
const MEMBER_DIRECTORY_ROLES = new Set(["group_admin", "facility_admin"]);

/**
 * Roles holding `onboarding.generate_qr_kit` (contracts `systemRoleGrants`,
 * spec 02 §7). Like `identity.view_members` above, it is NOT an `inventory.*`
 * code, so it stays out of INVENTORY_ROLE_GRANTS (whose parity test is against
 * the inventory slice) — but §4.3's "Print QR labels" needs it, because POST
 * /locations/qr-labels is gated on it: printing a label is the same act
 * whichever screen starts it (spec 06 §5 row 5). Like the member directory,
 * it is read off the feed's permission list and falls back to this set.
 */
const LABEL_PRINT_ROLES = new Set(["group_admin", "facility_admin", "storekeeper"]);

export interface InventoryPermissions extends AreaPermissions, RoleSignal {
  /** The caller's literal role codes; a role-shaped rule reads the lineage
   * through `hasLineage` instead (D-586). */
  readonly roleCodes: ReadonlySet<string>;
  /** True when the caller may read `GET /v1/members` (principal names). */
  readonly canReadMembers: boolean;
  /** True when the caller holds `onboarding.generate_qr_kit` (§4.3 label batch). */
  readonly canPrintLabels: boolean;
  /**
   * True when the role feed FAILED (as opposed to "settled with no grants").
   * A custom tenant role resolves to zero grants by design, so a read surface
   * must not turn "we cannot tell" into "you are not permitted" — it lets the
   * request go and renders the server's own refusal.
   */
  readonly isFeedError: boolean;
}

/**
 * The caller's inventory permissions. Fail-closed while the role feed is in
 * flight or has failed: screens render read-only rather than flashing a control
 * the server will refuse.
 */
export function useInventoryPermissions(): InventoryPermissions {
  const feed = useRoleCodesFeed();
  const codes = deriveInventoryPermissions(feed);
  return {
    isResolved: feed.isResolved,
    has: (code: string) => codes.has(code),
    ...roleSignalOf(feed),
    canReadMembers: holdsPermission(feed, "identity.view_members", MEMBER_DIRECTORY_ROLES),
    canPrintLabels: holdsPermission(feed, "onboarding.generate_qr_kit", LABEL_PRINT_ROLES),
    isFeedError: feed.isFeedError,
  };
}
